import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { usePrevious } from 'react-use';

import { useWizardContext } from '../context';
import * as Interfaces from '../interfaces';

/**
 * custom hook to persist the wizard context "values" data back to the storage when it gets changed
 *
 * Ref:
 *
 * > https://github.com/jaredpalmer/formik-persist
 * > https://github.com/jaredpalmer/formik-persist/issues/23
 * @param storage storage data
 */
export default function usePersist<T>(
  formProps: FormikProps<T>,
  storage?: Interfaces.WizardStorageProps<T>
) {
  // Read the wizard context values
  const { formValues, formTouched, formErrors, version, dispatch } = useWizardContext();
  const { setValues, errors, setErrors, touched, setTouched } = formProps;

  // extract relevant storage methods
  // const {
  //   key = '',
  //   getItem = () => JSON.parse('{}'),
  //   setItem = () => {},
  //   removeItem = () => {},
  //   loadOnMount = true
  // } = storage ?? {};
  const memoStorage = useMemo(() => {
    return (
      storage ?? {
        key: '',
        getItem: () => JSON.parse('{}'),
        setItem: () => {},
        removeItem: () => {},
        loadOnMount: true
      }
    );
  }, [storage]);

  // Runs the event on mount to take browser storage db values (if there were before) into the formik context
  useEffect(() => {
    if (memoStorage.key) {
      const maybeInState = memoStorage.getItem(memoStorage.key);
      if (!isEmpty(maybeInState)) {
        if (memoStorage.loadOnMount) {
          // setValues(maybeInState);
          dispatch({
            type: 'INIT_VALUES',
            values: maybeInState
          });
        } else {
          memoStorage.removeItem(memoStorage.key);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist changes from the form "values" back to the browser storage db
  const previousVersion = usePrevious(version);
  useEffect(() => {
    // only on storage key been provided, then persist the value to storage upon change to the Wizard context `values` collection
    if (previousVersion && !isEqual(previousVersion, version)) {
      if (!isEmpty(formValues)) {
        setValues(formValues);
        memoStorage.key && memoStorage.setItem(memoStorage.key, formValues);
      }

      // only on "errors" change need to persist the coming "SET_FORM_PROPS" action payload into formik context using built-in "setErrors" method
      if (!isEmpty(formErrors) && !isEqual(formErrors, errors)) {
        setErrors(formErrors as FormikErrors<T>);
      }

      // only on "touched" change need to persist the coming "SET_FORM_PROPS" action payload into formik context using built-in "setTouched" method
      if (!isEmpty(formTouched) && !isEqual(formTouched, touched)) {
        setTouched(formTouched as FormikTouched<T>, false);
      }
    }
  }, [
    formValues,
    memoStorage,
    previousVersion,
    version,
    setValues,
    setTouched,
    setErrors,
    formErrors,
    errors,
    formTouched,
    touched
  ]);
}
